import * as React from 'react';
import { Camera, Upload } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import type { CapturedImage } from './types';

/**
 * Screenshot capture entry point (plan §5.3 / D4, D5).
 *
 * Primary path: `navigator.mediaDevices.getDisplayMedia()` opens the native
 * "whole screen / window / tab" picker, we grab a single frame at native
 * resolution and stop the tracks immediately (no ongoing screen recording).
 *
 * Fallback (feature-detected, degrades silently): browsers without
 * `getDisplayMedia` — mobile, older Safari — and any insecure/iframed context
 * get a file input plus a paste-image drop zone into the same flow.
 */

export interface ScreenCaptureProps {
  /** Called with a decoded image once capture/upload/paste succeeds. */
  onCapture: (image: CapturedImage) => void;
  disabled?: boolean;
}

/**
 * `getDisplayMedia` requires a secure context and does not work inside a
 * cross-origin iframe. Feature-detect all three so we can hide the button
 * rather than surface an error the user cannot act on.
 */
function canUseDisplayMedia(): boolean {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') return false;
  if (!navigator.mediaDevices || typeof navigator.mediaDevices.getDisplayMedia !== 'function') {
    return false;
  }
  if (window.isSecureContext === false) return false;
  try {
    if (window.self !== window.top) return false; // inside an iframe
  } catch {
    return false; // cross-origin frame access threw → treat as iframed
  }
  return true;
}

async function bitmapFromBlob(blob: Blob): Promise<CapturedImage> {
  const bitmap = await createImageBitmap(blob);
  return { bitmap, width: bitmap.width, height: bitmap.height };
}

export function ScreenCapture({ onCapture, disabled = false }: ScreenCaptureProps): JSX.Element {
  const [busy, setBusy] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const supportsCapture = React.useMemo(canUseDisplayMedia, []);

  const captureScreen = React.useCallback(async () => {
    setBusy(true);
    let stream: MediaStream | null = null;
    try {
      stream = await navigator.mediaDevices.getDisplayMedia({ video: true });
      const video = document.createElement('video');
      video.srcObject = stream;
      video.muted = true;

      await new Promise<void>((resolve, reject) => {
        video.onloadeddata = () => resolve();
        video.onerror = () => reject(new Error('Could not read the captured stream'));
        void video.play().catch(reject);
      });

      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Canvas 2D context unavailable');
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      // Stop the capture immediately — we only ever wanted one frame.
      stream.getTracks().forEach((track) => track.stop());
      stream = null;

      const bitmap = await createImageBitmap(canvas);
      onCapture({ bitmap, width: bitmap.width, height: bitmap.height });
    } catch (err) {
      // User dismissing the picker rejects with NotAllowedError/AbortError —
      // that is a normal cancel, not an error worth surfacing.
      const name = err instanceof DOMException ? err.name : '';
      if (name !== 'NotAllowedError' && name !== 'AbortError') {
        toast.error('Screenshot capture failed. Try uploading an image instead.');
      }
    } finally {
      stream?.getTracks().forEach((track) => track.stop());
      setBusy(false);
    }
  }, [onCapture]);

  const handleFile = React.useCallback(
    async (file: File | undefined | null) => {
      if (!file) return;
      if (!file.type.startsWith('image/')) {
        toast.error('Please choose an image file.');
        return;
      }
      setBusy(true);
      try {
        onCapture(await bitmapFromBlob(file));
      } catch {
        toast.error('Could not read that image.');
      } finally {
        setBusy(false);
      }
    },
    [onCapture],
  );

  // Listen for pasted images (Cmd/Ctrl+V) as a fallback capture path.
  const onPaste = React.useCallback(
    (event: React.ClipboardEvent) => {
      const item = Array.from(event.clipboardData?.items ?? []).find((i) =>
        i.type.startsWith('image/'),
      );
      if (!item) return;
      event.preventDefault();
      void handleFile(item.getAsFile());
    },
    [handleFile],
  );

  return (
    <div className="flex flex-col gap-2" onPaste={onPaste}>
      <div className="flex flex-wrap items-center gap-2">
        {supportsCapture && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={captureScreen}
            disabled={disabled || busy}
          >
            <Camera className="mr-2 h-4 w-4" />
            Capture screenshot
          </Button>
        )}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || busy}
        >
          <Upload className="mr-2 h-4 w-4" />
          Upload image
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="sr-only"
          aria-label="Upload screenshot image"
          onChange={(e) => {
            void handleFile(e.target.files?.[0]);
            e.target.value = ''; // allow re-selecting the same file
          }}
        />
      </div>
      {!supportsCapture && (
        <p className="text-xs text-muted-foreground">
          Screen capture isn&rsquo;t available in this browser — upload an image or paste one from
          your clipboard.
        </p>
      )}
    </div>
  );
}
